import React, { useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTitle } from 'react-use';

import { BUTTON_TYPES, NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import {
  buildPageTitle,
  SUPPORTED_ICON_POSITIONS,
} from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { OneProductPerClaimBanner } from '@savgroup-front-common/core/molecules/OneProductPerClaimBanner';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';

import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import { useHasForceOneProductPerClaim } from '../../../app/hooks/useHasForceOneProductPerClaim';
import { InitContext } from '../../../app/NewLayout/InitProvider/InitProvider.context';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { IrshStepValues } from '../IrshPages.types';

import { ChooseProductPageContent } from './ChooseProductPageContent';
import messages from './messages';
import useChooseProduct from './NewChooseProductPage.hooks';

export const NewChooseProductPage: React.FC<
  RoutedStepProps<IrshStepValues>
> = ({ onPreviousStep, onNextStep }) => {
  const { openedFiles, orders = [] } = useContext(InitContext);
  const hasForceOneProductPerClaim = useHasForceOneProductPerClaim();

  const {
    formContext,
    onSubmit,
    isLoadingSubmit,
    isIssuesLoading,
    hasNoEligibleProduct,
  } = useChooseProduct({ onNextStep });

  const {
    formState: { isValid },
  } = formContext;

  useTitle(buildPageTitle([messages.chooseProductsTitlePage]));

  if (isIssuesLoading) {
    return <BaseLoader />;
  }

  return (
    <FormProvider {...formContext}>
      <form onSubmit={onSubmit}>
        <NewStepLayout stepName={STEP_CONFIG.CHOOSE_PRODUCTS}>
          {hasForceOneProductPerClaim && <OneProductPerClaimBanner />}
          {hasNoEligibleProduct && (
            <Banner
              isOpen
              hollow
              withMarginBottom
              notificationType={NOTIFICATION_TYPES.INFO}
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.noEligibleProductBanner}
              />
            </Banner>
          )}
          {orders.map((order) => {
            return (
              <ChooseProductPageContent
                key={order.orderId}
                orderId={order.orderId}
                openedFiles={openedFiles}
              />
            );
          })}
        </NewStepLayout>
        <PageFooterContainer>
          <Button
            primary
            type={BUTTON_TYPES.BUTTON}
            icon={<ArrowNakedRightIcon />}
            position={SUPPORTED_ICON_POSITIONS.RIGHT}
            disabled={!isValid}
            isLocked={hasNoEligibleProduct}
            lockedMessage={messages.noEligibleProductLock}
            isLoading={isLoadingSubmit}
            onClick={onSubmit}
            dataTestId="nextButton"
          >
            <SafeFormattedMessageWithoutSpread message={messages.nextButton} />
          </Button>

          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={() => {
              onPreviousStep({});
            }}
            icon={<ArrowNakedLeftIcon />}
            position={SUPPORTED_ICON_POSITIONS.LEFT}
            dataTestId="previousButton"
          >
            <SafeFormattedMessageWithoutSpread
              message={messages.previousButton}
            />
          </Button>
        </PageFooterContainer>
      </form>
    </FormProvider>
  );
};
